import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, inject, Injector, Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { parseUrl, fromSearchString, nonNullable, updateUsvObject, getSearchStringValue, isNumeric, Debounce, isEmpty, fusion } from '@js-dot/core';
import { Subject, BehaviorSubject, Subscription, take, from, of, zip, debounceTime as debounceTime$1 } from 'rxjs';
import * as i1 from '@angular/router';
import { NavigationStart, NavigationEnd, ActivationEnd, ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { debounceTime, map, distinctUntilChanged, first, takeUntil, skip, filter } from 'rxjs/operators';
import { __decorate } from 'tslib';
import { combineLatests$, observify } from '@js-dot/rx';
import { DotModalContainerDirective } from '@ng-dot/ux/modal';
class DotRouteService {
  _injector;
  _router;
  _activateRoute;
  reduced$ = new Subject();
  _queryParams = {};
  _segments = new WeakMap();
  get path() {
    const {
      url
    } = this._router;
    return url.match(/^([^?#]+)/)[1];
  }
  get params() {
    return this._queryParams;
  }
  constructor(_injector, _router, _activateRoute) {
    this._injector = _injector;
    this._router = _router;
    this._activateRoute = _activateRoute;
    _activateRoute.queryParams.subscribe(params => {
      this._queryParams = params;
    });
  }
  getActivatedRoute() {
    let route = this._router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }
  reduce(params) {
    this.reduced$.next(params);
  }
  moveParameters(url_or_path, query) {
    if (Array.isArray(url_or_path)) {
      url_or_path = url_or_path.join('/');
    } else if (url_or_path === null) {
      url_or_path = this.path;
    }
    let pathname;
    if (url_or_path) {
      const parsedUrl = parseUrl(url_or_path);
      if (parsedUrl) {
        pathname = parsedUrl.pathname;
        if (!query) {
          query = parsedUrl.search;
        }
      }
    }
    const commands = pathname ? pathname.replace(/\/$/, '').split('/') : '';
    const searchParams = typeof query === 'string' ? fromSearchString(query) : query;
    return {
      commands,
      queryParams: nonNullable(searchParams)
    };
  }
  updateParameters(url_or_path, query) {
    const {
      commands
    } = this.moveParameters(url_or_path, query);
    return {
      commands,
      queryParams: updateUsvObject(query, this._queryParams)
    };
  }
  move$(url_or_path, query) {
    const {
      commands,
      queryParams
    } = this.moveParameters(url_or_path, query);
    return this._router.navigate(commands, {
      queryParams,
      relativeTo: url_or_path.startsWith('.') ? this.getActivatedRoute() : null
    });
  }
  update$(url_or_path, query) {
    const {
      commands,
      queryParams
    } = this.updateParameters(url_or_path, query);
    return this._router.navigate(commands, {
      queryParams
    });
  }
  /**
   * Navigate to parent route
   */
  parent$() {
    const {
      _segments
    } = this;
    const activatedRoute = this.getActivatedRoute();
    const {
      routeConfig
    } = activatedRoute;
    if (!_segments.has(routeConfig)) {
      let parent = activatedRoute.parent;
      // while (!parent.component) {
      // 	parent = parent.parent!;
      // }
      const parentRoutes = parent.pathFromRoot;
      const end = parentRoutes.length;
      let current = -1;
      const segments = [''];
      while (++current < end) {
        const {
          url
        } = parentRoutes[current].snapshot;
        const urlEnd = url.length;
        let urlCurrent = -1;
        while (++urlCurrent < urlEnd) {
          const {
            path
          } = url[urlCurrent];
          if (path) {
            segments[segments.length] = path;
          }
        }
      }
      _segments.set(routeConfig, segments.slice(0, segments.length - 1));
    }
    return this._router.navigate(_segments.get(routeConfig));
  }
  refresh$() {
    return this.update$(null, {
      refresh: Date.now()
    });
  }
  refresh() {
    return this.refresh$().then();
  }
  redirect(queryName = 'redirect') {
    const to = this._queryParams[queryName];
    this.move$(to ? decodeURIComponent(to) : '/').then();
  }
  static ɵfac = function DotRouteService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRouteService)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i1.Router, 8), i0.ɵɵinject(i1.ActivatedRoute, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRouteService,
    factory: DotRouteService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRouteService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i0.Injector
  }, {
    type: i1.Router,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1.ActivatedRoute,
    decorators: [{
      type: Optional
    }]
  }], null);
})();
class DotRouteEventService {
  _router;
  _document;
  url$ = new BehaviorSubject(null);
  started$ = new Subject();
  _ended$ = new BehaviorSubject(null);
  ended$ = new Subject();
  url;
  _path$;
  path$;
  constructor(_router, _document) {
    this._router = _router;
    const {
      documentElement
    } = _document;
    this._document = documentElement;
    if (!_router) {
      return;
    }
    this._path$ = new BehaviorSubject(null);
    this.path$ = this._path$.pipe(debounceTime(24), map(getActivatedRoutePath));
    _router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.url = event.url;
        this.started$.next(this.url);
      } else if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects || event.url;
        this.url = url;
        this.ended$.next(url);
        this.url$.next(url);
      } else if (event instanceof ActivationEnd) {
        this._path$.next(event.snapshot);
      }
    });
  }
  query(key) {
    return getSearchStringValue(this.url, key);
  }
  static ɵfac = function DotRouteEventService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRouteEventService)(i0.ɵɵinject(i1.Router, 8), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRouteEventService,
    factory: DotRouteEventService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRouteEventService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.Router,
    decorators: [{
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
function getActivatedRoutePath(snapshot) {
  return '/' + getActivatedRoutePathRecursive(snapshot).join('/');
}
function getActivatedRoutePathRecursive(snapshot, segments = []) {
  const {
    routeConfig: {
      path
    },
    firstChild
  } = snapshot;
  if (path) {
    segments[segments.length] = path;
  }
  if (firstChild) {
    return getActivatedRoutePathRecursive(firstChild, segments);
  }
  return segments;
}
const DISTINCT_UNTIL_CHANGED_FUNCTION = distinctUntilChanged();
const DEBOUNCE_TIME_FUNCTION = debounceTime(48);
const CAST_MAP_FUNCTION = map(value => {
  return isNumeric(value) ? +value : value;
});
const SHARED_POOL = {
  slug: {},
  query: {}
};
/**
 * @deprecated Using DotUrlService
 * @description
 *   this service makes easy to using the path parameter, query string and the resolvers
 */
class DotRoutePageService {
  _injector;
  _route;
  _routeEvent;
  _router;
  _activatedRoute;
  _document;
  subscriptions = new Subscription();
  _dataPool = {};
  _requested = {
    slug: [],
    query: []
  };
  _complexSubjects = [];
  _lastUrl;
  _resolves;
  _resolvers = {};
  _resolveData;
  _firstResolved;
  _setResolved;
  _expendParams;
  _destroy$ = new Subject();
  changed$ = new Subject();
  set subscription(subscription) {
    this.subscriptions.add(subscription);
  }
  constructor(_injector, _route, _routeEvent, _router, _activatedRoute, _document) {
    this._injector = _injector;
    this._route = _route;
    this._routeEvent = _routeEvent;
    this._router = _router;
    this._activatedRoute = _activatedRoute;
    this._document = _document;
    const routeConfig = _activatedRoute.routeConfig || {};
    this._resolves = routeConfig.resolve || {};
    this._resolveData = _activatedRoute.snapshot.data;
    this.subscriptions.add(this._routeEvent.url$.pipe(take(1)).subscribe(this._updates.bind(this)));
    this.subscriptions.add(this._routeEvent.ended$.subscribe(this._updates.bind(this)));
    // if ((routeConfig.children || []).some(hasExpandPath)) {
    // 	this.subscription = _route.expanded$
    // 		.subscribe(expandParams => {
    // 			if (expandParams) {
    // 				this._expendParams = expandParams;
    // 			}
    // 		});
    //
    // 	this.subscription = _route.reduced$
    // 		.subscribe(expandParams => {
    // 			if (expandParams) {
    // 				this._expendParams = null;
    // 				Object.keys(expandParams).forEach(key => {
    // 					const item = SHARED_POOL.slug[key];
    // 					if (item) {
    // 						item.subject.next(null);
    // 					}
    // 				});
    // 			}
    // 		});
    //
    // 	this.subscription = _route.expandResolve$
    // 		.subscribe(response => {
    // 			const [resolveMap, resolve] = response;
    //
    // 			this._resolves = {
    // 				...this._resolves,
    // 				...resolveMap
    // 			};
    //
    // 			this._resolveData = {
    // 				...this._resolveData,
    // 				...resolve
    // 			};
    // 		});
    // }
  }
  /**
   * @deprecated Do not use
   */
  with(component) {
    console.warn('[DEPRECATED] `DotRoutePageService` are no longer need to call `with()`');
    return this;
  }
  /**
   * @deprecated Do not use
   */
  destroy() {
    console.warn('[DEPRECATED] `DotRoutePageService` are no longer need to call `destroy()`');
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this._destroy$.next();
    this.changed$.complete();
    const localPools = [this._dataPool];
    let poolCurrent = localPools.length;
    while (poolCurrent-- > 0) {
      const pool = localPools[poolCurrent];
      for (let key in pool) {
        if (pool.hasOwnProperty(key)) {
          pool[key].subject.complete();
        }
      }
    }
    const {
      _requested
    } = this;
    for (let type in _requested) {
      if (!_requested.hasOwnProperty(type)) {
        continue;
      }
      const keys = _requested[type];
      let keyCurrent = keys.length;
      while (keyCurrent-- > 0) {
        const pool = SHARED_POOL[type];
        const key = keys[keyCurrent];
        const item = pool[key];
        if (item) {
          if (--item.count < 1) {
            item.subject.complete();
            delete pool[key];
          }
        }
      }
    }
    this._next = () => {};
    this._update = () => {};
    this._updates = () => {};
  }
  /**
   * update router params, data(resolvers)
   * @private
   */
  _updates(url) {
    if (url === this._lastUrl) {
      return;
    }
    this._lastUrl = url;
    const {
      _resolves
    } = this;
    const {
      snapshot
    } = this._activatedRoute;
    const params = mergeParams(snapshot);
    if (params) {
      this._update('slug', params);
    }
    this._update('query', fromSearchString(this._document.location.search));
    if (_resolves) {
      if (this._firstResolved) {
        const {
          _resolvers,
          _injector
        } = this;
        for (let key in _resolves) {
          if (!_resolves.hasOwnProperty(key)) {
            continue;
          }
          let resolver = _resolvers[key];
          if (!resolver) {
            resolver = _resolvers[key] = _injector.get(_resolves[key]);
          }
          let subject = resolver.resolve(snapshot, this._router.routerState.snapshot);
          subject = subject instanceof Promise ? from(subject) : subject['subscribe'] && typeof subject['subscribe'] === 'function' ? subject : of(subject);
          subject.pipe(first()).subscribe(value => {
            this._next('data', key, value);
          });
        }
      } else {
        this._firstResolved = true;
        this._update('data', this._resolveData);
      }
    }
    this.changed$.next();
  }
  _getPool(type) {
    return type === 'data' ? this._dataPool : SHARED_POOL[type];
  }
  _update(type, values) {
    const pool = this._getPool(type);
    for (let key in pool) {
      if (pool.hasOwnProperty(key) && !values.hasOwnProperty(key)) {
        this._next(type, key, values[key]);
      }
    }
    for (let key in values) {
      if (values.hasOwnProperty(key)) {
        this._next(type, key, values[key]);
      }
    }
  }
  _next(type, key, value) {
    const pool = this._getPool(type);
    let item = pool[key];
    if (item) {
      item.subject.next(value);
    } else {
      const subject = new BehaviorSubject(value);
      item = {
        subject,
        subjectWithCast: subject.pipe(CAST_MAP_FUNCTION),
        subjectWithFilter: subject.pipe(DISTINCT_UNTIL_CHANGED_FUNCTION, CAST_MAP_FUNCTION),
        count: 0
      };
      pool[key] = item;
    }
    return item;
  }
  _getSubjects(type, conditions) {
    const firstItem = conditions[0];
    let complexSubject;
    const getItem = this._getSubject.bind(this, type);
    if (Array.isArray(firstItem)) {
      complexSubject = zip(...firstItem.map(key => getItem(key).subjectWithCast)).pipe(DEBOUNCE_TIME_FUNCTION, map(values => {
        return values.reduce((_map, value, index) => {
          _map[firstItem[index]] = value;
          return _map;
        }, {});
      }));
    } else if (typeof firstItem === 'object') {
      const keys = Object.keys(firstItem);
      complexSubject = zip(...keys.map(key => getItem(key, firstItem[key]).subjectWithCast)).pipe(DEBOUNCE_TIME_FUNCTION, map(subjects => {
        return subjects.reduce((_map, subject, index) => {
          _map[keys[index]] = subject;
          return _map;
        }, {});
      }));
    }
    if (complexSubject) {
      this._complexSubjects.push(complexSubject);
      return complexSubject;
    }
    // error will occurred with not execute the `page.with`
    return this._getSubject(type, firstItem, conditions[1]).subjectWithFilter.pipe(takeUntil(this._destroy$));
  }
  _getSubject(type, key, defaults) {
    this._requested[type].push(key);
    const pool = this._getPool(type);
    let item = pool[key];
    if (item) {
      item.count++;
    } else {
      if (defaults === undefined) {
        const skipCount = this._lastUrl ? 0 : 1;
        const subject = new BehaviorSubject(undefined);
        item = {
          subject,
          subjectWithCast: subject.pipe(CAST_MAP_FUNCTION),
          subjectWithFilter: subject.pipe(skip(skipCount), DISTINCT_UNTIL_CHANGED_FUNCTION, CAST_MAP_FUNCTION),
          count: 1
        };
      } else {
        const subject = new BehaviorSubject(defaults);
        item = {
          subject,
          subjectWithCast: subject.pipe(CAST_MAP_FUNCTION),
          subjectWithFilter: subject.pipe(map(response => {
            return response === undefined ? defaults : response;
          }), DISTINCT_UNTIL_CHANGED_FUNCTION),
          count: 1
        };
      }
      pool[key] = item;
    }
    return item;
  }
  /**
   * Get Slug or Slugs as Map
   * @param conditions
   */
  slug$(...conditions) {
    return this._getSubjects('slug', conditions);
  }
  /**
   * Get Query string or Query strings as Map
   * @param conditions
   */
  query$(...conditions) {
    return this._getSubjects('query', conditions);
  }
  hasData(key) {
    return this._resolves[key];
  }
  hasSlug(key) {
    return !!this._activatedRoute.snapshot.params[key];
  }
  /**
   * Get Route Resolve Data or Dataset as Map
   * @param keys
   */
  data$(...keys) {
    const {
      _resolves,
      _dataPool
    } = this;
    const subjects = keys.map(key => {
      let item = _dataPool[key];
      if (!item) {
        const subject = new BehaviorSubject(null);
        item = {
          subject,
          subjectWithCast: null,
          subjectWithFilter: subject.pipe(skip(1)),
          count: 1
        };
        _dataPool[key] = item;
      }
      if (!_resolves[key]) {
        item.subject.error(`Cannot found Route.resolve.${key}`);
      }
      return item;
    });
    if (keys.length === 1) {
      return subjects[0].subjectWithFilter;
    }
    return zip(...subjects.map(item => item.subject)).pipe(map(responses => {
      return keys.reduce((stringHas, key, index) => {
        stringHas[key] = responses[index];
        return stringHas;
      }, {});
    }));
  }
  static ɵfac = function DotRoutePageService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRoutePageService)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(DotRouteService), i0.ɵɵinject(DotRouteEventService), i0.ɵɵinject(i1.Router), i0.ɵɵinject(i1.ActivatedRoute), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRoutePageService,
    factory: DotRoutePageService.ɵfac
  });
}
__decorate([Debounce(48)], DotRoutePageService.prototype, "_updates", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRoutePageService, [{
    type: Injectable
  }], () => [{
    type: i0.Injector
  }, {
    type: DotRouteService
  }, {
    type: DotRouteEventService
  }, {
    type: i1.Router
  }, {
    type: i1.ActivatedRoute
  }, {
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], {
    _updates: []
  });
})();
function mergeParams(snapshot) {
  const {
    children
  } = snapshot;
  let {
    params
  } = snapshot;
  if (children) {
    let current = children.length;
    while (current-- > 0) {
      const childParams = children[current].params;
      if (childParams) {
        params = {
          ...params,
          ...childParams
        };
      }
    }
  }
  return params;
}
const URL_CASTER_EXTEND_NAME_INDEX = {};
class RxUrlSearchCaster {
  _destroy$ = new Subject();
  _takeUntilOperator = takeUntil(this._destroy$);
  _debounceTimeOperator = debounceTime$1(240);
  _values = {};
  _providers = {};
  _extendsStringMap = {};
  _extendsWeakMap = new WeakMap();
  constructor() {
    this._values['search'] = new Map();
    this._providers['search'] = new Map();
  }
  _getExtendClass() {
    return RxUrlExtendSearchCaster;
  }
  _set(pool, values) {
    const providers = this._providers[pool];
    this._values[pool] = values;
    for (const [key, {
      subject
    }] of providers) {
      if (values.has(key)) {
        const value = values.get(key);
        if (subject.value !== value) {
          subject.next(value);
        }
      } else {
        subject.next(undefined);
      }
    }
  }
  /**
   * Set Search String
   * @param searchString
   */
  setSearch(searchString) {
    this._set('search', new URLSearchParams(searchString));
  }
  get$(key_or_keys_or_map, defaultValue) {
    let target = key_or_keys_or_map;
    if (Array.isArray(target)) {
      const subjects = [];
      let current = target.length;
      while (current-- > 0) {
        subjects[current] = this.get$(target[current]);
      }
      return combineLatests$(subjects).pipe(map(values => {
        const result = {};
        let current = subjects.length;
        while (current-- > 0) {
          result[target[current]] = values[current];
        }
        return result;
      }));
    }
    if (typeof target === 'object') {
      const keys = [];
      const subjects = [];
      for (let key in target) {
        if (!target.hasOwnProperty(key)) {
          continue;
        }
        keys[keys.length] = key;
        subjects[subjects.length] = this.get$(key, target[key]);
      }
      return subjects.length ? combineLatests$(subjects).pipe(map(values => {
        const result = {};
        let current = subjects.length;
        while (current-- > 0) {
          result[keys[current]] = values[current];
        }
        return result;
      })) : of({});
    }
    if (!target.startsWith('$')) {
      throw new RangeError('UrlSearchCaster support search-string only');
    }
    let pool = 'search';
    target = target.slice(1);
    const providers = this._providers[pool];
    const store = this._values[pool];
    const operators = [this._takeUntilOperator];
    let subject;
    if (store.has(target)) {
      subject = new BehaviorSubject(store.get(target));
    } else {
      subject = new BehaviorSubject(defaultValue);
      if (defaultValue === undefined) {
        operators[operators.length] = skip(1);
      }
    }
    if (defaultValue !== undefined) {
      operators[operators.length] = map(value => value || defaultValue);
    }
    operators[operators.length] = distinctUntilChanged();
    const observable = subject.pipe.apply(subject, operators);
    providers.set(target, {
      subject,
      observable
    });
    return observable;
  }
  extend(key) {
    const extendClass = this._getExtendClass();
    if (typeof key === 'string') {
      const {
        _extendsStringMap
      } = this;
      return _extendsStringMap[key] || (_extendsStringMap[key] = new extendClass(this, key));
    }
    const {
      _extendsWeakMap
    } = this;
    if (!_extendsWeakMap.has(key)) {
      const constructorName = key.constructor.name;
      const extendIndex = URL_CASTER_EXTEND_NAME_INDEX[constructorName] ? ++URL_CASTER_EXTEND_NAME_INDEX[constructorName] : URL_CASTER_EXTEND_NAME_INDEX[constructorName] = 1;
      _extendsWeakMap.set(key, new extendClass(this, constructorName + extendIndex));
    }
    return _extendsWeakMap.get(key);
  }
  destroy() {
    this._destroy$.next();
  }
  set(key, value) {}
  pop(key) {}
}
class RxUrlExtendSearchCaster extends RxUrlSearchCaster {
  caster;
  extendKey;
  constructor(caster, extendKey) {
    super();
    this.caster = caster;
    if (!extendKey.startsWith('$')) {
      extendKey = '$' + extendKey;
    }
    this._values['search'] = new URLSearchParams('');
    this.extendKey = extendKey;
    caster.get$(extendKey).subscribe(value => {
      value && this.setSearch(decodeURIComponent(value));
    });
  }
  set(key, value) {
    if (!key.startsWith('$')) {
      throw new RangeError('Does not support set the parameters value in SearchStringCaster');
    }
    key = key.slice(1);
    const values = this._values['search'];
    values.set(key, value);
    this.caster.set(this.extendKey, '' + values);
  }
  destroy() {
    this.caster.pop(this.extendKey);
    super.destroy();
  }
}

/**
 * Route(URL) Values Operator
 */
class DotSelfUrlService extends RxUrlSearchCaster {
  _resolves;
  _resolvers = {};
  _resolveData;
  _lastUrl;
  changed$ = new Subject();
  _injector;
  _route;
  _routeEvent;
  _router;
  _activatedRoute;
  _document;
  constructor() {
    super();
    const injector = inject(Injector);
    const _activatedRoute = injector.get(ActivatedRoute);
    const _routeEvent = injector.get(DotRouteEventService);
    this._injector = injector;
    this._route = injector.get(DotRouteService);
    this._routeEvent = _routeEvent;
    this._router = injector.get(Router);
    this._activatedRoute = _activatedRoute;
    this._document = injector.get(DOCUMENT);
    this._values['path'] = new Map();
    this._providers['path'] = new Map();
    this._values['data'] = new Map();
    this._providers['data'] = new Map();
    const routeConfig = _activatedRoute.routeConfig || {};
    this._resolveData = _activatedRoute.snapshot.data;
    this._resolvers = routeConfig.resolve || {};
    const selfOnRouteEnd = this._onRouteEnd.bind(this);
    // Fixed: First Load
    _routeEvent.url$.pipe(take(1), this._takeUntilOperator).subscribe(selfOnRouteEnd);
    _routeEvent.ended$.pipe(this._takeUntilOperator).subscribe(selfOnRouteEnd);
  }
  // protected override _getExtendClass() {
  // 	return super._getExtendClass();
  // }
  ngOnDestroy() {
    super.destroy();
  }
  _onRouteEnd(url) {
    if (url === this._lastUrl) {
      return;
    }
    this._lastUrl = url;
    const {
      snapshot
    } = this._activatedRoute;
    const parameters = getLiftedParameters(snapshot);
    if (parameters) {
      this._set('path', new Map(Object.entries(parameters)));
    }
    this._set('search', snapshot.queryParamMap);
    // const {_resolves} = this;
    this.changed$.next();
  }
  get$(key_or_keys_or_map, defaultValue) {
    let target = key_or_keys_or_map;
    if (Array.isArray(target)) {
      if (target.length > 1) {
        const subjects = [];
        let current = target.length;
        while (current-- > 0) {
          subjects[current] = this.get$(target[current]);
        }
        return combineLatests$(subjects).pipe(map(values => {
          const result = {};
          let current = subjects.length;
          while (current-- > 0) {
            result[target[current]] = values[current];
          }
          return result;
        }));
      }
      target = target[0];
    }
    if (typeof target === 'object') {
      const keys = [];
      const subjects = [];
      for (let key in target) {
        if (!target.hasOwnProperty(key)) {
          continue;
        }
        keys[keys.length] = key;
        subjects[subjects.length] = this.get$(key, target[key]);
      }
      return combineLatests$(subjects).pipe(map(values => {
        const result = {};
        if (values) {
          if (!Array.isArray(values)) {
            values = [values];
          }
          let current = subjects.length;
          while (current-- > 0) {
            result[keys[current]] = values[current];
          }
        }
        return result;
      }));
    }
    let pool = 'path';
    if (target.startsWith('$')) {
      pool = 'search';
      target = target.slice(1);
    }
    const providers = this._providers[pool];
    const store = this._values[pool];
    const operators = [this._takeUntilOperator];
    let subject;
    if (store.has(target)) {
      subject = new BehaviorSubject(store.get(target));
    } else {
      subject = new BehaviorSubject(defaultValue);
      if (defaultValue === undefined) {
        // console.warn(target, 'skip1');
        // operators[operators.length] = skip(1);
        // let first                   = true;
        // operators[operators.length] = filter(value => {
        // 	if (value === undefined && first) {
        // 		first = false;
        // 		return false;
        // 	}
        // 	first = false;
        // 	return true;
        // });
      }
    }
    if (defaultValue !== undefined) {
      operators[operators.length] = map(value => value || defaultValue);
    }
    operators[operators.length] = distinctUntilChanged();
    const observable = subject.pipe.apply(subject, operators);
    providers.set(target, {
      subject,
      observable
    });
    return observable;
  }
  set(key_or_map, value) {
    let map = key_or_map;
    if (typeof key_or_map === 'string') {
      map = {
        [key_or_map]: value
      };
    }
    const searchStrings = {};
    for (let key in map) {
      if (!map.hasOwnProperty(key)) {
        continue;
      }
      value = map[key];
      if (key.startsWith('$')) {
        searchStrings[key.slice(1)] = value;
      } else {
        throw new Error('Not Implemented For Set Parameter Variables');
      }
    }
    this._route.update$('', searchStrings);
    return this;
  }
  switch(key, value) {
    const {
      _values
    } = this;
    if (key.startsWith('$')) {
      if (_values['search'].get(key.slice(1)) == value) {
        this.pop(key);
      } else {
        this.set(key, value);
      }
    } else {
      // todo: implement
    }
  }
  /**
   * Remove URL Value
   *
   * @alias this.set(key, null)
   * @example
   * Remove `?bar`:
   * ```js
   * _user.pop('$bar');
   * ```
   * @param key
   */
  pop(key) {
    return this.set(key, null);
  }
  static ɵfac = function DotSelfUrlService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotSelfUrlService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotSelfUrlService,
    factory: DotSelfUrlService.ɵfac
  });
}
__decorate([Debounce()], DotSelfUrlService.prototype, "_onRouteEnd", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotSelfUrlService, [{
    type: Injectable
  }], () => [], {
    _onRouteEnd: []
  });
})();
function getLiftedParameters(snapshot) {
  let {
    params,
    parent
  } = snapshot;
  while (parent) {
    params = {
      ...parent.params,
      ...params
    };
    parent = parent.parent;
  }
  // const {children} = snapshot;
  // let current      = children.length;
  //
  // while (current-- > 0) {
  // 	const childParams = children[current].params;
  // 	if (childParams) {
  // 		params = {
  // 			...params,
  // 			...childParams
  // 		};
  // 	}
  // }
  return params;
}
class DotUrlExtendCaster extends DotSelfUrlService {}
const FETCH_ABORT_SUBJECTS = new WeakMap();
/**
 * Http Resolve Pattern with URL Parameters for Each Component
 * @example
 * ```ts
 * @Component({
 *   standalone: true,
 *   providers: [
 *       DotSelfResolveService
 *   ],
 *   template: `
 *   	{{data$ | async}}
 *   `
 * })
 * export class FooComponent implements OnInit {
 * 	   data$ = new BehaviorSubject<any>();
 *     constructor(protected _resolve: DotSelfResolveService){
 *       _resolve.load({
 *           parameters: ['$limit', '$page'],
 *           respond: this.data$
 *       });
 *     }
 * }
 * ```
 */
class DotSelfFetcherService extends DotSelfUrlService {
  _fetcherOption;
  _fetcherRawParameters = {};
  fetchInit;
  /**
   * Response Body
   */
  fetch$ = new Subject();
  /**
   * First Fetch State
   */
  fetchFirst$ = new BehaviorSubject(true);
  /**
   * Loading State
   */
  fetchLoad$ = new BehaviorSubject(true);
  /**
   * Error State
   */
  fetchError$ = new BehaviorSubject(false);
  fetchAbort$ = new Subject();
  modal;
  _caster = this;
  _prevParameters;
  constructor() {
    super();
    const modal = this._injector.get(DotModalContainerDirective, null);
    if (modal) {
      this.modal = modal;
      this.setCaster(modal.modal.modalId || modal.modal);
    }
  }
  _viewInit;
  ngOnDestroy() {
    this.fetch$.complete();
    this.fetchFirst$.complete();
    this.fetchLoad$.complete();
    this.fetchError$.complete();
    this._prevParameters = null;
    this._caster.destroy();
    super.ngOnDestroy();
  }
  /**
   * Load Http Response
   * @param option
   */
  load(option) {
    if (this.fetchInit) {
      throw new ReferenceError(`${this.constructor.name} is already loaded.`);
    }
    this.fetchInit = true;
    this._fetcherOption = option;
    const {
      _caster
    } = this;
    const requiredFilterFilter = filter(this._requiredFilterMethod.bind(this));
    observify(this.getParameters()).subscribe(parameters => {
      if (isEmpty(parameters)) {
        this.reload();
      } else {
        _caster.get$(parameters).pipe(requiredFilterFilter).subscribe(this._loadSubscribeMethod.bind(this));
      }
      _caster.get$('$refresh').subscribe(refresh => {
        if (!!refresh) {
          _caster.pop('$refresh');
          this.reload();
        }
      });
    });
  }
  _requiredFilterMethod(values) {
    const {
      required
    } = this._fetcherOption;
    if (required) {
      let current = required.length;
      while (current-- > 0) {
        const name = required[current];
        if (values[name] === undefined) {
          return false;
        }
      }
      return true;
    }
    return true;
  }
  _loadSubscribeMethod(values) {
    if (this._urlParameterValidation(values)) {
      return false;
    }
    this._fetcherRawParameters = values;
    this.reload();
    return true;
  }
  /**
   * Reload Http Response
   */
  reload() {
    const {
      constructor,
      _fetcherRawParameters,
      _fetcherOption: {
        requester,
        respond
      },
      fetchLoad$,
      fetchError$
    } = this;
    fetchLoad$.next(true);
    fetchError$.next(false);
    requester.request(this.setParameters({
      ..._fetcherRawParameters
    })).pipe(takeUntil(this.fetchAbort$), this._takeUntilOperator).subscribe({
      next: response => {
        fetchLoad$.next(false);
        if (!response) {
          return fetchError$.next(true);
        }
        //////////////////////////////
        // using dot-render
        this.fetch$.next(response);
        //////////////////////////////
        // using respond property
        if (!respond) {
          return;
        }
        if (typeof respond === 'function') {
          return respond(response);
        }
        if (typeof respond['next'] === 'function') {
          return respond['next'](response);
        }
      },
      error: reason => {
        console.debug(reason);
        fetchLoad$.next(false);
        fetchError$.next(true);
      }
    });
  }
  getParameters() {
    return this._fetcherOption.parameters;
  }
  setParameters(rawParameters) {
    return rawParameters;
  }
  setCaster(name) {
    this._caster = this.extend(name);
  }
  /**
   * API 중복 요청이 발생하여 route에는 url 에서 filter, sort 값이 있지만 values 에 없으면 반환하도록 추가
   * @author kdh
   * @param values
   * @protected
   */
  _urlParameterValidation(values) {
    if (values) {
      const $filter = values?.$filter;
      const $sort = values?.$sort;
      const params = this._route.params;
      const {
        filter,
        sort
      } = params;
      if (!filter && !sort) {
        return false;
      }
      if (!$filter && !!filter || !$sort && !!sort) {
        return true;
      }
      if (this._prevParameters && this._prevParameters === params) {
        return true;
      }
      this._prevParameters = params;
    }
    return false;
  }
  abort() {
    this.fetchAbort$.next();
  }
  static ɵfac = function DotSelfFetcherService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotSelfFetcherService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotSelfFetcherService,
    factory: DotSelfFetcherService.ɵfac
  });
}
__decorate([Debounce()], DotSelfFetcherService.prototype, "_loadSubscribeMethod", null);
__decorate([Debounce()], DotSelfFetcherService.prototype, "reload", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotSelfFetcherService, [{
    type: Injectable
  }], () => [], {
    _loadSubscribeMethod: [],
    reload: []
  });
})();

/**
 * @deprecated
 */
class DotSelfListResolveService extends DotSelfFetcherService {
  load(option) {
    return super.load(fusion({
      parameters: {
        $limit: 24,
        $page: 1,
        $sort: null,
        $filter: null
      }
    }, option));
  }
  static ɵfac = /* @__PURE__ */(() => {
    let ɵDotSelfListResolveService_BaseFactory;
    return function DotSelfListResolveService_Factory(__ngFactoryType__) {
      return (ɵDotSelfListResolveService_BaseFactory || (ɵDotSelfListResolveService_BaseFactory = i0.ɵɵgetInheritedFactory(DotSelfListResolveService)))(__ngFactoryType__ || DotSelfListResolveService);
    };
  })();
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotSelfListResolveService,
    factory: DotSelfListResolveService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotSelfListResolveService, [{
    type: Injectable
  }], null, null);
})();

/**
 * This component just do routing children route
 */
class DotRouteOutletComponent {
  static ɵfac = function DotRouteOutletComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRouteOutletComponent)();
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotRouteOutletComponent,
    selectors: [["dot-route-outlet"]],
    decls: 1,
    vars: 0,
    template: function DotRouteOutletComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "router-outlet");
      }
    },
    dependencies: [RouterOutlet],
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRouteOutletComponent, [{
    type: Component,
    args: [{
      imports: [RouterOutlet],
      selector: 'dot-route-outlet',
      template: '<router-outlet></router-outlet>',
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DotRouteEventService, DotRouteOutletComponent, DotRoutePageService, DotRouteService, DotSelfFetcherService, DotSelfListResolveService, DotSelfUrlService, getLiftedParameters };
